import { createFileRoute } from '@tanstack/react-router';
import { Construction, Trophy } from 'lucide-react';

import { StateChip } from '@/components/primitives/StateChip';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import { useLeaguePlayoffs } from '@/features/leagues/league-detail';

export const Route = createFileRoute('/leagues/$leagueId/playoffs')({
  component: PlayoffsTab,
});

function PlayoffsTab() {
  const { leagueId } = Route.useParams();
  const { data, isPending, isError, error, refetch } = useLeaguePlayoffs(leagueId);

  if (isError) {
    return (
      <ErrorState error={error} subject="the playoff bracket" onRetry={() => void refetch()} />
    );
  }
  if (isPending) return <Skeleton height={220} />;

  if (!data.bracket) {
    return (
      <StateBlock
        icon={Trophy}
        tone="neutral"
        title={data.reason === 'no_season' ? 'No season' : 'Playoffs not seeded yet'}
        description={
          // These are genuinely different situations and an operator acts on them
          // differently, so they are not collapsed into one "nothing here".
          data.reason === 'no_season'
            ? 'This league has no season, so there is no bracket to seed.'
            : 'The season exists but the bracket has not been seeded. Seeding happens upstream when the regular season completes.'
        }
      />
    );
  }

  const bracket = data.bracket;

  return (
    <div className="flex flex-col gap-4">
      <section className="border-bd bg-card rounded-xl border p-3.5">
        <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
          <h2
            className="font-head text-tx2 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
            style={{ letterSpacing: '0.16em' }}
          >
            <Trophy size={12} aria-hidden />
            Bracket
          </h2>
          {bracket.status && <StateChip state={bracket.status} size="sm" />}
        </div>

        <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Round"
            value={`${bracket.currentRound ?? '?'} of ${bracket.maxRounds ?? '?'}`}
          />
          <Field label="Teams" value={bracket.playoffTeams?.toString() ?? '—'} />
          <Field
            label="Weeks"
            value={`${bracket.regularWeeks ?? '?'} regular + ${bracket.playoffWeeks ?? '?'} playoff`}
          />
          <Field label="Seeded" value={bracket.seededAt?.slice(0, 10) ?? 'not yet'} />
        </dl>

        {bracket.championName && (
          <p
            className="mt-2.5 rounded-lg border px-3 py-2 text-[13px]"
            style={{
              borderColor: 'rgba(var(--ok-rgb), 0.36)',
              background: 'rgba(var(--ok-rgb), 0.08)',
              color: 'var(--ok)',
            }}
          >
            Champion: {bracket.championName}
            {bracket.completedAt ? ` · completed ${bracket.completedAt.slice(0, 10)}` : ''}
          </p>
        )}
      </section>

      <section className="border-bd bg-card rounded-xl border p-3.5">
        <h2
          className="font-head text-tx2 mb-2.5 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          Seeding
          <span className="mesh-mono text-tx3 ml-1.5 text-[11px]">{bracket.seeds.length}</span>
        </h2>

        {bracket.seeds.length === 0 ? (
          <p className="text-tx3 text-[12.5px]">No seeds recorded.</p>
        ) : (
          <ol className="flex flex-col gap-1.5">
            {[...bracket.seeds]
              .sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99))
              .map((seed) => (
                <li
                  key={`${String(seed.seed)}-${seed.squadId ?? 'none'}`}
                  className="border-bd/60 flex items-center gap-2.5 border-b pb-1.5 last:border-b-0 last:pb-0"
                >
                  <span
                    aria-hidden
                    className="mesh-mono grid size-6 flex-none place-items-center rounded text-[11px] font-semibold"
                    style={{
                      background: 'rgba(var(--brand-rgb), 0.18)',
                      color: 'var(--brand-soft)',
                    }}
                  >
                    {seed.seed ?? '?'}
                  </span>
                  <span className="text-tx min-w-0 flex-1 truncate text-[13px]">
                    {seed.name}
                  </span>
                  {seed.squadId === bracket.championSquadId && (
                    <StateChip state="champion" size="sm" tone="good" label="Champion" />
                  )}
                </li>
              ))}
          </ol>
        )}
      </section>

      {bracket.rounds.length > 0 && (
        <section className="border-bd bg-card rounded-xl border p-3.5">
          <h2
            className="font-head text-tx2 mb-2.5 text-[11px] font-semibold uppercase"
            style={{ letterSpacing: '0.16em' }}
          >
            Rounds
          </h2>
          <ul className="flex flex-wrap gap-2">
            {bracket.rounds.map((round) => (
              <li
                key={`${String(round.round)}-${String(round.week)}`}
                className="border-bd bg-elev rounded-lg border px-2.5 py-1.5"
              >
                <span className="mesh-mono text-tx2 text-[12px]">
                  R{round.round ?? '?'} · W{round.week ?? '?'}
                </span>
                {round.round === bracket.currentRound && (
                  <span className="ml-1.5">
                    <StateChip state="active" size="sm" />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Manual bracket correction changes who is eliminated. It has no delegate
        operation and no upstream endpoint, so there is nothing to offer here yet.
      */}
      <section className="border-bd bg-card rounded-xl border p-3.5">
        <h2
          className="font-head text-tx2 mb-2.5 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          Bracket correction
        </h2>
        <StateBlock
          icon={Construction}
          tone="info"
          title="Not available"
          description="Editing a bracket changes who is eliminated, and seeding is derived from standings ZazzBackend computes. There is no delegate operation and no upstream endpoint for it, so nothing is offered here rather than a control that would refuse."
        />
      </section>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt
        className="font-head text-tx3 text-[10px] font-semibold uppercase"
        style={{ letterSpacing: '0.14em' }}
      >
        {label}
      </dt>
      <dd className="mesh-mono text-tx2 mt-0.5 truncate text-[12.5px]">{value}</dd>
    </div>
  );
}
